import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPhone,
  faCheck,
  faChevronLeft,
  faChevronRight,
  faArrowRight,
  faTemperatureLow,
  faRoad,
  faTruckMoving,
  faCubes,
  faHouseChimney,
  faLeaf,
} from '@fortawesome/free-solid-svg-icons';
import Header from './Header';
import CTAButton from './CTAButton';
import EmailAndPhoneButton from './EmailAndPhoneButton';
import SEOHead from './SEOHead';
import Kontaktformular from './Kontaktformular';
import { useServiceImages } from '../hooks/useServiceImages';
import kontaktBaggrund from '../images/jbcBillede2.jpg';
import '../pages/styles/ServicePage.css';

const alleTjenester = [
  { titel: 'Jordvarme', path: '/jordvarme', icon: faTemperatureLow },
  { titel: 'Belægning', path: '/belægning', icon: faRoad },
  { titel: 'Fundamenter', path: '/fundamenter', icon: faCubes },
  { titel: 'Tilbygning', path: '/tilbygning', icon: faHouseChimney },
  { titel: 'Midlertidig vej', path: '/midlertidig-vej', icon: faTruckMoving },
  { titel: 'LCA & Bæredygtighed', path: '/lca', icon: faLeaf },
];

function Galleri({ slug, navn }) {
  const { images, loading } = useServiceImages(slug);
  const [aktiv, setAktiv] = useState(0);
  const [pause, setPause] = useState(false);
  const timer = useRef(null);
  const reduceMotion = typeof window !== 'undefined'
    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  useEffect(() => {
    if (images.length <= 1 || pause || reduceMotion) return undefined;
    timer.current = setInterval(() => setAktiv((i) => (i + 1) % images.length), 5000);
    return () => clearInterval(timer.current);
  }, [images.length, pause, reduceMotion]);

  if (loading) {
    return (
      <section className="tj-galleri" aria-busy="true" aria-label={`Projekter inden for ${navn}`}>
        <div className="tj-galleri-skelet" />
      </section>
    );
  }

  if (images.length === 0) return null;

  const gaa = (retning) => setAktiv((i) => (i + retning + images.length) % images.length);
  const billede = images[aktiv];

  return (
    <section className="tj-galleri" aria-labelledby="galleri-titel">
      <div className="sektion-intro">
        <span className="hero-eyebrow">Udvalgte projekter</span>
        <h2 id="galleri-titel">Se noget af vores arbejde</h2>
      </div>
      <div
        className="tj-galleri-ramme"
        onMouseEnter={() => setPause(true)}
        onMouseLeave={() => setPause(false)}
        onFocus={() => setPause(true)}
        onBlur={() => setPause(false)}
      >
        <figure className="tj-galleri-hoved">
          <img
            key={billede.url}
            src={billede.url}
            alt={billede.caption || `${navn} – projekt ${aktiv + 1}`}
            style={{
              objectPosition: billede.focusPoint
                ? `${billede.focusPoint.x}% ${billede.focusPoint.y}%`
                : '50% 50%',
            }}
          />
          {billede.caption && <figcaption>{billede.caption}</figcaption>}
          {images.length > 1 && (
            <>
              <button type="button" className="tj-galleri-pil venstre" onClick={() => gaa(-1)} aria-label="Forrige billede">
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <button type="button" className="tj-galleri-pil hoejre" onClick={() => gaa(1)} aria-label="Næste billede">
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
              <span className="tj-galleri-taeller" aria-live="polite">
                {aktiv + 1} / {images.length}
              </span>
            </>
          )}
        </figure>
        {images.length > 1 && (
          <div className="tj-galleri-thumbs">
            {images.map((img, i) => (
              <button
                type="button"
                key={img.url}
                className={i === aktiv ? 'aktiv' : ''}
                onClick={() => setAktiv(i)}
                aria-label={`Vis billede ${i + 1}`}
                aria-current={i === aktiv ? 'true' : undefined}
              >
                <img src={img.url} alt="" loading="lazy" />
              </button>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function ServicePage({
  slug,
  path,
  navn,
  seo,
  hero,
  intro,
  ydelser,
  fordele,
  ekstra,
  kontaktTekst,
}) {
  const navigate = useNavigate();
  const tilKontakt = () => navigate('/kontakt');

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: seo.serviceName || navn,
    serviceType: navn,
    description: seo.description,
    url: `https://jbcentreprenoeren.dk${encodeURI(path)}`,
    provider: { '@id': 'https://jbcentreprenoeren.dk/#business' },
    areaServed: [
      { '@type': 'AdministrativeArea', name: 'Holbæk Kommune' },
      { '@type': 'AdministrativeArea', name: 'Sjælland' },
    ],
  };

  return (
    <div className="tj">
      <SEOHead
        title={seo.title}
        description={seo.description}
        url={path}
        image={seo.image}
        structuredData={structuredData}
        breadcrumbs={[{ name: 'Hjem', url: '/' }, { name: navn, url: path }]}
      />
      <Header />
      <EmailAndPhoneButton />

      <section className="tj-hero">
        <div className="tj-hero-inner">
          <div className="tj-hero-tekst">
            <nav className="tj-brodkrumme" aria-label="Brødkrumme">
              <Link to="/">Hjem</Link>
              <span aria-hidden="true">/</span>
              <span aria-current="page">{navn}</span>
            </nav>
            <h1>{hero.titel}</h1>
            <p>{hero.tekst}</p>
            <div className="tj-hero-actions">
              <CTAButton text={hero.cta || 'Få et gratis tilbud'} onClick={tilKontakt} />
              <a href="tel:+4522780476" className="hero-phone-link">
                <span className="hero-phone-icon">
                  <FontAwesomeIcon icon={faPhone} />
                </span>
                <span>
                  <small>Ring til os</small>
                  22 78 04 76
                </span>
              </a>
            </div>
          </div>
          <div className="tj-hero-billede">
            <img src={hero.billede} alt={hero.billedeAlt} />
          </div>
        </div>
      </section>

      <section className="tj-intro" aria-labelledby="tj-intro-titel">
        <div className="tj-intro-inner">
          <div className="tj-intro-tekst">
            <span className="hero-eyebrow">{navn}</span>
            <h2 id="tj-intro-titel">{intro.titel}</h2>
            {intro.tekst.map((afsnit, i) => (
              <p key={i}>{afsnit}</p>
            ))}
            {ekstra}
          </div>
          <aside className="tj-ydelser" aria-labelledby="tj-ydelser-titel">
            <h3 id="tj-ydelser-titel">{ydelser.titel}</h3>
            <ul>
              {ydelser.punkter.map((p) => (
                <li key={p}>
                  <span className="tj-check" aria-hidden="true"><FontAwesomeIcon icon={faCheck} /></span>
                  {p}
                </li>
              ))}
            </ul>
            <CTAButton text={hero.cta || 'Få et gratis tilbud'} onClick={tilKontakt} />
          </aside>
        </div>
      </section>

      <section className="tj-fordele" aria-labelledby="tj-fordele-titel">
        <div className="sektion-intro">
          <span className="hero-eyebrow">Derfor JBC Entreprenøren</span>
          <h2 id="tj-fordele-titel">{fordele.titel}</h2>
        </div>
        <ul className="tj-fordele-liste">
          {fordele.punkter.map((p) => (
            <li key={p}>
              <span className="tj-check stor" aria-hidden="true"><FontAwesomeIcon icon={faCheck} /></span>
              <span>{p}</span>
            </li>
          ))}
        </ul>
      </section>

      <Galleri slug={slug} navn={navn} />

      <section className="proces" aria-labelledby="proces-titel">
        <div className="proces-inner">
          <span className="hero-eyebrow">Sådan foregår det</span>
          <h2 id="proces-titel">Fra første opkald til færdigt arbejde</h2>
          <ol className="proces-liste">
            <li>
              <span className="proces-nr" aria-hidden="true">1</span>
              <h3>Du kontakter os</h3>
              <p>Ring på <a href="tel:+4522780476">22 78 04 76</a> eller skriv via formularen, og fortæl kort om opgaven.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">2</span>
              <h3>Vi tager en snak</h3>
              <p>Vi gennemgår dine ønsker og kigger gerne på stedet, så vi forstår opgaven rigtigt.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">3</span>
              <h3>Du får et tilbud</h3>
              <p>Gratis og uforpligtende. Du bestemmer selv, om du vil gå videre.</p>
            </li>
            <li>
              <span className="proces-nr" aria-hidden="true">4</span>
              <h3>Vi udfører arbejdet</h3>
              <p>Vi går i gang som aftalt og leverer et ordentligt stykke arbejde.</p>
            </li>
          </ol>
        </div>
      </section>

      <section className="tj-andre" aria-labelledby="tj-andre-titel">
        <div className="sektion-intro">
          <span className="hero-eyebrow">Andre tjenester</span>
          <h2 id="tj-andre-titel">Vi kan også hjælpe med</h2>
        </div>
        <ul className="tj-andre-liste">
          {alleTjenester.filter((t) => t.path !== path).map((t) => (
            <li key={t.path}>
              <Link to={t.path}>
                <span className="tj-andre-ikon" aria-hidden="true"><FontAwesomeIcon icon={t.icon} /></span>
                <strong>{t.titel}</strong>
                <FontAwesomeIcon icon={faArrowRight} className="tj-andre-pil" aria-hidden="true" />
              </Link>
            </li>
          ))}
        </ul>
      </section>

      <div className="kontakt-section" style={{ backgroundImage: `url(${kontaktBaggrund})` }}>
        <div className="kontakt-inner">
          <div className="kontakt-content">
            <h2>Kontakt os og få et tilbud</h2>
            <p>{kontaktTekst}</p>
            <div className="kontakt-info">
              <p><strong>JBC Entreprenøren ApS</strong></p>
              <p>Naurvej 6, Undløse</p>
              <p>4340 Tølløse</p>
              <p>CVR nr. 44384345</p>
              <p>Tlf: <a href="tel:+4522780476">22 78 04 76</a></p>
              <p>Mail: <a href="mailto:jbc@jbcentreprenoeren.dk">jbc@jbcentreprenoeren.dk</a></p>
            </div>
          </div>
          <div className="kontakt-formular">
            <Kontaktformular />
          </div>
        </div>
      </div>
    </div>
  );
}

export default ServicePage;
